import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { usePrescriptionsByVisitId } from '../../hooks/usePrescriptions';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { Button } from '../common/Button';
import { FileText, Plus, Eye } from 'lucide-react';
import { formatDate } from '../../utils/formatters';
import { useNavigate } from 'react-router-dom';

interface PrescriptionsListProps {
  visitId: number;
  onPrescriptionClick: (prescriptionId: number) => void;
}

export const PrescriptionsList: React.FC<PrescriptionsListProps> = ({
  visitId,
  onPrescriptionClick,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const navigate = useNavigate();
  const { data: prescriptions, isLoading, error } = usePrescriptionsByVisitId(visitId);

  // Filter prescriptions to ensure they belong to the correct visit
  const filteredPrescriptions = React.useMemo(() => {
    if (!prescriptions) return [];
    // Double-check that all prescriptions belong to this visit
    return prescriptions.filter(p => p.visitId === visitId);
  }, [prescriptions, visitId]);

  if (isLoading) {
    return (
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <div className="d-flex justify-content-center py-3">
            <LoadingSpinner size="sm" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <h5 className="fw-bold mb-3">{t('prescriptions.title')}</h5>
          <p className="text-danger text-center py-3 mb-0">
            {t('errors.loadingError', 'حدث خطأ في تحميل الوصفات الطبية')}
          </p>
        </div>
      </div>
    );
  }

  if (!filteredPrescriptions || filteredPrescriptions.length === 0) {
    return (
      <div className="card shadow-sm border-0">
        <div className="card-body">
          <h5 className="fw-bold mb-3">{t('prescriptions.title')}</h5>
          <p className="text-muted text-center py-3 mb-0">
            {t('prescriptions.noPrescriptions', 'لا توجد وصفات طبية لهذه الزيارة')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <div className="d-flex align-items-center justify-content-between mb-3" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
          <h5 className="fw-bold mb-0">{t('prescriptions.title')}</h5>
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(`/prescriptions/new?visitId=${visitId}`)}
            className="d-flex align-items-center gap-2"
            style={{ direction: isRTL ? 'rtl' : 'ltr' }}
          >
            <Plus size={16} />
            {t('prescriptions.newPrescription')}
          </Button>
        </div>
        <div className="list-group list-group-flush">
          {filteredPrescriptions.map((prescription) => (
            <div
              key={prescription.id}
              className="list-group-item border-0 border-bottom p-3"
              style={{
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <div className="d-flex align-items-center justify-content-between" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 mb-2" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                    <FileText className="text-primary" size={18} />
                    <p className="mb-0 fw-semibold text-dark">
                      {t('prescriptions.prescriptionDetails')} - {formatDate(prescription.createdAt)}
                    </p>
                  </div>
                  
                  {prescription.items && prescription.items.length > 0 && (
                    <p className="text-muted small mb-1">
                      {prescription.items.length} {t('prescriptions.medications')}
                    </p>
                  )}
                  
                  {(prescription.labTests?.length > 0 || prescription.radiologyTests?.length > 0) && (
                    <p className="text-muted small mb-0">
                      {prescription.labTests?.length || 0} {t('prescriptions.labTests', 'تحاليل')} /{' '}
                      {prescription.radiologyTests?.length || 0} {t('prescriptions.radiologyTests', 'أشعة')}
                    </p>
                  )}
                </div>
                <Button
                  variant="text"
                  size="sm"
                  onClick={(e) => {
                    e?.stopPropagation();
                    onPrescriptionClick(prescription.id);
                  }}
                  className="text-info p-2"
                  title={t('prescriptions.viewDetails', 'عرض التفاصيل')}
                >
                  <Eye size={18} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

